import { createTheme, ThemeOptions } from '@mui/material/styles';
import { getAnxietyColor } from '../utils/anxietyColors';

// Déclaration pour étendre la palette
declare module '@mui/material/styles' {
  interface Palette {
    badge: {
      achieved: {
        background: string;
        text: string;
      };
      locked: {
        background: string;
        text: string;
      };
    };
  }
  interface PaletteOptions {
    badge: {
      achieved: {
        background: string;
        text: string;
      };
      locked: {
        background: string;
        text: string;
      };
    };
  }
}

// Autoriser la variante personnalisée 'sidebar' pour les boutons MUI
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    sidebar: true;
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: '#FF4B8C', // Rose vif
      light: '#FF8E6E', // Orange clair
      dark: '#1A1B35', // Bleu très foncé
    },
    secondary: {
      main: '#2E3192', // Bleu profond
      light: '#5C6BC0',
      dark: '#1A237E',
    },
    background: {
      default: '#1A1B35', // Fond bleu foncé
      paper: '#252644', // Un peu plus clair pour les cartes
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    badge: {
      achieved: {
        background: '#ffb74d',
        text: '#000000'
      },
      locked: {
        background: 'rgba(255,255,255,0.1)',
        text: '#375051'
      }
    }
  },
  shape: {
    borderRadius: 40,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(45deg, #FF4B8C 30%, #FF8E6E 90%)',
          borderRadius: 35,
          padding: '16px',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #FF4B8C 0%, #2E3192 100%)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(45deg, #68f1d1 20%,#ff4b8c 100%)',
          borderRadius: 25,
          textTransform: 'none',
        },
        contained: {
          background: 'linear-gradient(45deg, #ff4b8c 20%,#FF8E6E 100%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #5ad1b1 20%,#df3b7c 100%)',
          },
        },
        outlined: {
          background: 'transparent',
          borderColor: '#68f1d1',
          color: '#68f1d1',
          '&:hover': {
            borderColor: '#ff4b8c',
            color: '#ff4b8c',
          },
        },
      },
      variants: [
        {
          props: { variant: 'sidebar' },
          style: {
            // Supprime le gradient global pour les boutons de navigation de la sidebar
            background: 'none',
            backgroundImage: 'none',
            boxShadow: 'none',
            borderRadius: 4,
            textTransform: 'none',
            color: 'inherit',
            '&:hover': {
              background: 'rgba(41, 40, 40, 0.96)',
              backgroundImage: 'none',
              boxShadow: 'none',
            },
            '&.Mui-disabled': { opacity: 0.6 },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        },
        elevation1: {
          boxShadow: '0 6px 8px rgba(0,0,0,0.4)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0,0,0,0.9)',
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(0,0,0,0.9)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #ff4b8c 0%, #2E3192 90%)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
          '&:before': {
            display: 'none', // Supprime la ligne de séparation
          },
          '&.Mui-expanded': {
            //background: 'linear-gradient(90deg, #1A1B35 30%, #306165 90%)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {          
          '&.Mui-expanded': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(185deg, #1A1B35 30%, #709DB8 120%)',
          borderBottomLeftRadius: '25px',
          borderBottomRightRadius: '25px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiLinearProgress-root': {
            height: '10px !important',
            borderRadius: '6px !important',
          },
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(45deg, #68f1d1 40%, #2E3192 100%)',
            //backgroundImage: 'none !important',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
    },
  },
});

export default theme;
